import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import { Post } from '@/types/post-management.types';
import { useUpdatePostMutation } from '@/services/PostService';
import { useSnackbar } from 'notistack';
import { KeenIcon } from '@/components';
import { I18N_LANGUAGES } from '@/i18n';
import { formatDisplayDate } from '@/utils/Date';
import MediaUpload from '@/pages/habor-management/MediaUpload';
import { ImageCaptionDTO } from '@/types/api.types';
import {
  ensureTranslationsForTextFields,
  getTranslationWarningMessage,
  isTranslationFailure,
} from '@/utils/translationUtils';
import { useIntl } from 'react-intl';

interface PostModalProps extends Omit<ModalProps, 'children'> {
  post: Post | null;
  mode?: 'view' | 'edit';
  communityId: string;
}
const PostModal = ({
  post,
  open,
  onClose,
  className,
  mode = 'view',
  communityId,
}: PostModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const [updatePost, { isLoading: isUpdating }] = useUpdatePostMutation();
  const [editData, setEditData] = useState({
    title: '',
    content: '',
    languages: ['en'],
    tags: [] as string[],
    source_language: 'en',
  });
  const [translations, setTranslations] = useState<
    Record<string, { title: string; content: string }>
  >({});
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [tagInput, setTagInput] = useState('');

  // Media + captions state (used only in edit mode)
  type PostMediaForm = {
    photos?: (File | string)[] | null;
    photos_captions: ImageCaptionDTO[];
  };
  const [mediaForm, setMediaForm] = useState<PostMediaForm>({
    photos: null,
    photos_captions: [],
  });
  const [mediaErrors, setMediaErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (post && open) {
      setEditData({
        title: post.title || '',
        content: post.content || '',
        languages: post.languages || ['en'],
        tags: post.tags || [],
        source_language: post.source_language || 'en',
      });
      setTranslations(post.translations || {});
      setSelectedLanguage('en');

      // Initialise media form from post data
      const existingPhotos: string[] = post.photos ?? [];
      // Convert backend image_captions (translations) → ImageCaptionDTO (captions)
      const existingCaptions: ImageCaptionDTO[] = (
        post.image_captions ?? []
      ).map((c) => ({
        image_index: c.image_index,
        captions: c.translations as Record<string, string>,
      }));
      setMediaForm({
        photos: existingPhotos.length > 0 ? existingPhotos : null,
        photos_captions: existingCaptions,
      });
      setMediaErrors({});
    }
  }, [post, open]);

  const handleLanguageChange = (newLang: string) => {
    // Save current title and content to translations for the current selected language
    setTranslations((prev) => ({
      ...prev,
      [selectedLanguage]: {
        title: editData.title,
        content: editData.content,
      },
    }));

    // Load title and content for the new language if it exists
    const nextTranslation = translations[newLang] || { title: '', content: '' };
    setEditData((prev) => ({
      ...prev,
      title: nextTranslation.title,
      content: nextTranslation.content,
    }));

    setSelectedLanguage(newLang);
  };

  const handleTagAdd = () => {
    const trimmed = tagInput.trim();
    if (!trimmed) return;
    if (editData.tags.includes(trimmed)) {
      setTagInput('');
      return;
    }
    setEditData((prev) => ({
      ...prev,
      tags: [...prev.tags, trimmed],
    }));
    setTagInput('');
  };

  const handleTagRemove = (tagToRemove: string) => {
    setEditData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleClose = () => {
    onClose?.({}, 'backdropClick');
  };

  const handleSave = async () => {
    if (!post || !communityId) return;

    try {
      const sourceLang = editData.source_language || 'en';
      const languageList =
        editData.languages.length > 0 ? editData.languages : [sourceLang];

      const buildPayload = (
        translationsPayload: Record<string, { title: string; content: string }>
      ) => {
        const formData = new FormData();
        const primary = translationsPayload[sourceLang] ||
          translationsPayload['en'] || {
            title: editData.title,
            content: editData.content,
          };
        formData.append('title', primary.title || editData.title);
        formData.append('content', primary.content || editData.content);
        formData.append('source_language', sourceLang);
        formData.append('tags', JSON.stringify(editData.tags));
        formData.append('translations', JSON.stringify(translationsPayload));
        languageList.forEach((lang) => formData.append('languages', lang));

        const currentPhotos = mediaForm.photos;
        const existingPhotos: string[] = [];
        if (currentPhotos && currentPhotos.length > 0) {
          currentPhotos.forEach((photo) => {
            if (photo instanceof File) {
              formData.append('photos', photo);
            } else if (typeof photo === 'string') {
              existingPhotos.push(photo);
            }
          });
        }
        formData.append('existing_photos', JSON.stringify(existingPhotos));

        const photosCaptions = mediaForm.photos_captions;
        if (photosCaptions && photosCaptions.length > 0) {
          const backendCaptions = photosCaptions.map((c) => ({
            image_index: c.image_index,
            translations: c.captions ?? {},
          }));
          formData.append('image_captions', JSON.stringify(backendCaptions));
        }
        return formData;
      };

      let translationsPayload = ensureTranslationsForTextFields(
        {
          ...translations,
          [selectedLanguage]: {
            title: editData.title,
            content: editData.content,
          },
        },
        languageList,
        { title: editData.title, content: editData.content },
        ['title', 'content']
      );

      const translationWarningFallback = intl.formatMessage({
        id: 'POI.PREVIEW.TRANSLATION_ERROR',
      });

      let response;
      try {
        response = await updatePost({
          communityId,
          postId: post.id,
          data: buildPayload(translationsPayload),
        }).unwrap();
      } catch (firstError) {
        if (isTranslationFailure(firstError)) {
          enqueueSnackbar(translationWarningFallback, { variant: 'warning' });
          translationsPayload = ensureTranslationsForTextFields(
            {},
            languageList,
            { title: editData.title, content: editData.content },
            ['title', 'content']
          );
          response = await updatePost({
            communityId,
            postId: post.id,
            data: buildPayload(translationsPayload),
          }).unwrap();
        } else {
          throw firstError;
        }
      }

      const warning = getTranslationWarningMessage(
        response,
        translationWarningFallback
      );
      if (warning) {
        enqueueSnackbar(warning, { variant: 'warning' });
      }

      enqueueSnackbar('Post updated successfully', { variant: 'success' });
      handleClose();
    } catch (error) {
      console.error('Failed to update post:', error);
      if (isTranslationFailure(error)) {
        enqueueSnackbar(
          intl.formatMessage({ id: 'POI.PREVIEW.TRANSLATION_ERROR' }),
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar('Failed to update post', { variant: 'error' });
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={clsx('!flex items-center justify-center', className)}
      aria-labelledby="post-modal-title"
      aria-describedby="post-modal-description"
      data-testid="post-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-gray-100 p-8 w-full max-w-2xl mx-4 rounded-xl shadow-2xl flex flex-col"
        role="document"
      >
        <div className="flex justify-between items-center mb-6">
          <h1
            id="post-modal-title"
            className="text-2xl font-semibold text-gray-800"
          >
            Post Details
          </h1>
          <div className="relative">
            <select
              id="language-select"
              className="select select-bordered pl-8 pr-8 h-10 leading-normal text-sm"
              value={selectedLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              aria-label="Select language"
            >
              {I18N_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
            <img
              src="/media/flags/globe.svg"
              alt="Globe"
              className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>
        <div id="post-modal-description" className="sr-only">
          View post details
        </div>
        <div
          className="flex-1 overflow-y-auto max-h-[70vh] pl-1 pr-3 -mx-1 custom-scrollbar"
          role="region"
          aria-label="Post details"
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
            {/* Title */}
            <div className="col-span-1">
              <label
                htmlFor="post-title"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Title
              </label>
              {mode === 'edit' ? (
                <input
                  id="post-title"
                  type="text"
                  name="title"
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300 flex items-center px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editData.title}
                  onChange={(e) =>
                    setEditData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  autoComplete="off"
                  aria-label="Edit post title"
                />
              ) : (
                <input
                  id="post-title"
                  readOnly
                  name="title"
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300 bg-gray-100 flex items-center px-3 text-gray-900"
                  aria-label={`Post title: ${editData.title || 'N/A'}`}
                  value={editData.title || 'N/A'}
                  autoComplete="off"
                />
              )}
            </div>

            {/* Author */}
            <div className="col-span-1">
              <span
                id="post-author-label"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Author
              </span>
              <div
                className="input input-bordered w-full h-11 rounded-lg border-gray-300 bg-gray-100 flex items-center overflow-hidden px-3 text-gray-900"
                role="textbox"
                aria-readonly="true"
                aria-labelledby="post-author-label"
                aria-label={`Post author: ${(() => {
                  const authorName = post?.author?.name;
                  const authorEmail = post?.author?.email;
                  return authorName || authorEmail || 'N/A';
                })()}`}
              >
                <span
                  className="text-sm font-medium text-gray-900 truncate w-full"
                  aria-label={(() => {
                    const authorName = post?.author?.name;
                    const authorEmail = post?.author?.email;
                    return authorName || authorEmail || 'N/A';
                  })()}
                >
                  {(() => {
                    const authorName = post?.author?.name;
                    const authorEmail = post?.author?.email;
                    if (authorName && authorName.length > 20) {
                      return `${authorName.slice(0, 20)}...`;
                    } else if (authorName) {
                      return authorName;
                    } else if (authorEmail && authorEmail.length > 20) {
                      return `${authorEmail.slice(0, 20)}...`;
                    } else {
                      return authorEmail || 'N/A';
                    }
                  })()}
                </span>
              </div>
            </div>

            {/* Status */}
            <div className="col-span-1">
              <span
                id="post-status-label"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Status
              </span>
              <div
                className="input input-bordered w-full h-11 rounded-lg border-gray-300 bg-gray-100 flex items-center text-gray-900"
                role="status"
                aria-labelledby="post-status-label"
                aria-label={`Post status: ${post?.status || 'N/A'}`}
              >
                {post?.status || 'N/A'}
              </div>
            </div>

            {/* Created At */}
            <div className="col-span-1">
              <span
                id="post-created-at-label"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Created At
              </span>
              <div
                className="input input-bordered w-full h-11 rounded-lg border-gray-300 bg-gray-100 flex items-center text-gray-900"
                role="textbox"
                aria-readonly="true"
                aria-labelledby="post-created-at-label"
                aria-label={`Post created at: ${post?.created_at ? formatDisplayDate(new Date(post.created_at)) : 'N/A'}`}
              >
                {post?.created_at
                  ? formatDisplayDate(new Date(post.created_at))
                  : 'N/A'}
              </div>
            </div>

            {/* Tags */}
            <div className="col-span-2">
              {mode === 'edit' ? (
                <label
                  htmlFor="post-tags-input"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Tags
                </label>
              ) : (
                <span
                  id="post-tags-label"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Tags
                </span>
              )}
              {mode === 'edit' ? (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      id="post-tags-input"
                      type="text"
                      name="tags"
                      className="input input-bordered flex-1 h-11 rounded-lg border-gray-300 px-3"
                      placeholder="Add a tag"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleTagAdd();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleTagAdd}
                      className="btn btn-primary h-11 rounded-lg px-4"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {editData.tags.map((tag) => (
                      <div
                        key={tag}
                        className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900 contrast:bg-blue-950 text-blue-800 dark:text-blue-200 contrast:text-blue-200 text-sm font-medium px-2.5 py-1 rounded-lg"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleTagRemove(tag)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 focus:outline-none"
                          aria-label="Remove tag"
                        >
                          <KeenIcon
                            type="filled"
                            icon="cross"
                            className="text-xs"
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  className="flex flex-wrap gap-2"
                  aria-labelledby="post-tags-label"
                >
                  {(post?.tags || []).length > 0 ? (
                    post?.tags.map((tag) => (
                      <div
                        key={tag}
                        className="bg-blue-100 dark:bg-blue-900 contrast:bg-blue-950 text-blue-800 dark:text-blue-200 contrast:text-blue-200 text-sm font-medium px-2.5 py-0.5 rounded"
                      >
                        {tag}
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 contrast:text-white">
                      No tags
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="col-span-2">
              <label
                htmlFor="post-content"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Content
              </label>
              <div>
                {mode === 'edit' ? (
                  <textarea
                    id="post-content"
                    className="w-full input h-32 p-3 border border-gray-300 rounded-lg resize-none dark:border-gray-600 dark:focus:ring-blue-600"
                    value={editData.content}
                    onChange={(e) =>
                      setEditData((prev) => ({
                        ...prev,
                        content: e.target.value,
                      }))
                    }
                    aria-label="Edit post content"
                  />
                ) : (
                  <textarea
                    id="post-content"
                    readOnly
                    className="w-full h-32 p-3 border border-gray-300 rounded-lg bg-gray-100 resize-none"
                    value={editData.content || '-'}
                    aria-label="Post content"
                    aria-readonly="true"
                  />
                )}
              </div>
            </div>

            {/* Media */}
            <div className="col-span-2">
              {mode === 'edit' ? (
                <MediaUpload<PostMediaForm>
                  field="photos"
                  media={mediaForm.photos ?? []}
                  setFormData={
                    setMediaForm as unknown as Dispatch<
                      SetStateAction<Partial<PostMediaForm>>
                    >
                  }
                  errors={mediaErrors}
                  setErrors={setMediaErrors}
                  disabled={false}
                  allowedTypes={[
                    'image/jpeg',
                    'image/png',
                    'image/webp',
                    'image/gif',
                  ]}
                  maxFiles={10}
                  previewMode={false}
                  media_captions={mediaForm.photos_captions}
                />
              ) : (
                <div>
                  <span
                    id="post-media-label"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Media
                  </span>
                  <div aria-labelledby="post-media-label">
                    {post?.photos && post.photos.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {post.photos.map((src, i) =>
                          /\.(mp4|webm|ogg)$/i.test(src) ? (
                            <video
                              key={i}
                              src={src}
                              controls
                              className="w-48 h-32 object-cover rounded-lg"
                            />
                          ) : (
                            <img
                              key={i}
                              src={src}
                              alt={`Post photo ${i + 1}`}
                              className="w-48 h-32 object-cover rounded-lg"
                            />
                          )
                        )}
                      </div>
                    ) : (
                      <div className="text-gray-500 contrast:text-white">
                        No media available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Likes and Comments */}
            <div className="col-span-2 flex gap-4">
              <div>
                <label
                  htmlFor="post-likes"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Likes
                </label>
                <output
                  id="post-likes"
                  className="input input-bordered min-w-[6rem] px-4 h-11 rounded-lg border-gray-300 bg-gray-100 opacity-50 flex items-center contrast:text-white"
                  role="status"
                  aria-label={`${post?.likeCount ?? 0} likes`}
                >
                  {post?.likeCount ?? 0}
                </output>
              </div>
              <div>
                <label
                  htmlFor="post-comments"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Comments
                </label>
                <output
                  id="post-comments"
                  className="input input-bordered min-w-[6rem] px-4 h-11 rounded-lg border-gray-300 bg-gray-100 opacity-50 flex items-center contrast:text-white"
                  role="status"
                  aria-label={`${post?.comments?.length ?? 0} comments`}
                >
                  {post?.comments?.length ?? 0}
                </output>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button
            data-testid="cancel-button"
            type="button"
            onClick={handleClose}
            className="btn btn-sm btn-secondary btn-ghost text-gray-800 hover:bg-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {mode === 'edit' ? 'Cancel' : 'Close'}
          </button>
          {mode === 'edit' && (
            <button
              type="button"
              onClick={handleSave}
              disabled={isUpdating}
              className="btn btn-sm btn-primary rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
              aria-label="Save Changes"
            >
              {isUpdating && (
                <KeenIcon
                  type="filled"
                  icon="spinner"
                  className="animate-spin text-sm"
                />
              )}
              Save Changes
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export { PostModal };
